import type { ReactNode } from 'react'
import type { SessionSummary, Show } from '@/lib/server/shows'

import { useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { FadeLink } from '@/components/generals/fade-link'
import { Reveal, RevealLines } from '@/components/generals/reveal'
import { ShowPoster } from '@/components/shows/show-poster'
import { BookingPanel } from '@/components/shows/booking-panel'
import { addDays, dateKey, formatKey, formatTime, parseDateKey, weekdayOf } from '@/lib/time'

interface ShowsSectionProps {
    shows: Show[]
    sessions: SessionSummary[]
    todayKey: string
}

/** Three upcoming shows as posters, and a two-week calendar of every performance. */
export const ShowsSection = ({ shows, sessions, todayKey }: ShowsSectionProps): ReactNode => {
    const [panelShow, setPanelShow] = useState<Show | null>(null)
    const [selectedDay, setSelectedDay] = useState(todayKey)

    const featured = useMemo(() => {
        const order: string[] = []

        for (const session of sessions) {
            if (!order.includes(session.showSlug)) order.push(session.showSlug)
        }

        const ranked = [...shows].sort((a, b) => {
            const ai = order.indexOf(a.slug)
            const bi = order.indexOf(b.slug)

            return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi)
        })

        return ranked.slice(0, 3)
    }, [shows, sessions])

    const days = useMemo(() => Array.from({ length: 14 }, (_, index) => addDays(todayKey, index)), [todayKey])

    const byDay = useMemo(() => {
        const map: Record<string, SessionSummary[]> = {}

        for (const key of days) map[key] = []

        for (const session of sessions) {
            const key = dateKey(new Date(session.startsAt))

            map[key]?.push(session)
        }

        return map
    }, [sessions, days])

    const selected = byDay[selectedDay] ?? []

    return (
        <section id='shows' aria-labelledby='shows-title' className='relative py-28 md:py-36'>
            <div className='container-page'>
                <div className='grid gap-8 lg:grid-cols-12 lg:items-end'>
                    <div className='lg:col-span-7'>
                        <p className='label-mono text-brass'>
                            Planetarium shows
                        </p>
                        <RevealLines
                            id='shows-title'
                            lines={['Lie back.', 'The sky comes to you.']}
                            className='display-serif mt-5 text-5xl text-star md:text-7xl'
                        />
                    </div>
                    <Reveal variant='wipe' className='lg:col-span-5'>
                        <p className='text-star-2'>
                            A twelve-metre dome, one hundred and fifty reclining seats and a star projector that shows the sky exactly as it would look from the darkest place on Earth. Every show is introduced by one of our astronomers.
                        </p>
                    </Reveal>
                </div>

                {featured.length === 0 ? (
                    <p className='mt-16 text-star-2'>
                        Show times are being updated. Please check back shortly, or call the desk.
                    </p>
                ) : (
                    <div className='mt-16 grid gap-14 md:grid-cols-2 lg:grid-cols-3 lg:gap-10'>
                        {featured.map((show, index) => (
                            <Reveal key={show.slug} delay={index * 140}>
                                <ShowPoster
                                    show={show}
                                    index={index}
                                    todayKey={todayKey}
                                    upcoming={sessions.filter(session => session.showSlug === show.slug)}
                                    onBook={() => setPanelShow(show)}
                                />
                            </Reveal>
                        ))}
                    </div>
                )}

                <div className='mt-24 grid gap-10 border-t border-hair pt-12 lg:grid-cols-12'>
                    <div className='lg:col-span-5'>
                        <p className='label-mono text-star-3'>
                            Pick a date
                        </p>
                        <div className='mt-5 grid grid-cols-7 gap-1.5' role='group' aria-label='Choose a day in the next two weeks'>
                            {days.map(key => {
                                const count = byDay[key]?.length ?? 0
                                const parts = parseDateKey(key)
                                const active = key === selectedDay

                                return (
                                    <button
                                        key={key}
                                        type='button'
                                        onClick={() => setSelectedDay(key)}
                                        aria-pressed={active}
                                        aria-label={`${formatKey(key, 'long')}, ${count ? `${count} performance${count === 1 ? '' : 's'}` : 'closed'}`}
                                        disabled={count === 0}
                                        className={cn(
                                            'flex aspect-square flex-col items-center justify-center border transition-colors duration-500 disabled:cursor-default',
                                            active ? 'border-brass bg-brass/10 text-star' : 'border-hair text-star-2 hover:border-hair-2',
                                            count === 0 && 'opacity-35'
                                        )}
                                    >
                                        <span className='label-mono text-[0.55rem] text-star-3'>
                                            {formatKey(key, 'short').slice(0, 2)}
                                        </span>
                                        <span className='font-heading text-xl leading-none'>
                                            {parts.day}
                                        </span>
                                        <span className='mt-1 flex gap-0.5' aria-hidden>
                                            {Array.from({ length: Math.min(4, Math.ceil(count / 2)) }, (_, index) => (
                                                <span key={index} className={cn('size-1 rounded-full', active ? 'bg-brass' : 'bg-star-3')} />
                                            ))}
                                        </span>
                                    </button>
                                )
                            })}
                        </div>
                    </div>

                    <div className='lg:col-span-7 lg:pl-8'>
                        <p className='label-mono text-star-3' aria-live='polite'>
                            {`${formatKey(selectedDay, 'long')} · ${selected.length} performance${selected.length === 1 ? '' : 's'}`}
                        </p>
                        {selected.length === 0 ? (
                            <p className='mt-5 text-star-2'>
                                {weekdayOf(selectedDay) === 1 ? 'The planetarium is closed on Mondays. Choose another day.' : 'No more performances on this day. Choose another day.'}
                            </p>
                        ) : (
                            <ul className='mt-4'>
                                {selected.map(session => (
                                    <li key={session.id}>
                                        <FadeLink
                                            href={session.available > 0 ? `/shows/book/${session.id}` : '/shows'}
                                            className='group grid grid-cols-[4.5rem_1fr_auto] items-baseline gap-4 border-b border-hair py-4 transition-colors duration-500 hover:border-brass/60'
                                        >
                                            <span className='data-mono text-lg text-star'>
                                                {formatTime(new Date(session.startsAt))}
                                            </span>
                                            <span>
                                                <span className='block font-heading text-2xl text-star'>
                                                    {session.showTitle}
                                                </span>
                                                <span className='block text-sm text-star-3'>
                                                    {`${session.kind} · ${session.duration} minutes · ${session.ageLabel}`}
                                                </span>
                                            </span>
                                            <span className={cn('label-mono text-[0.6rem]', session.available <= 0 ? 'text-star-3' : session.available < 20 ? 'text-brass' : 'text-star-2')}>
                                                {session.available <= 0 ? 'Sold out' : session.available < 20 ? `${session.available} left` : 'Choose seats'}
                                            </span>
                                        </FadeLink>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <FadeLink href='/shows' className='group mt-8 inline-flex items-center gap-3 text-star'>
                            <span className='h-px w-8 bg-brass transition-all duration-500 group-hover:w-12' />
                            <span className='label-mono'>
                                Full calendar and ticket prices
                            </span>
                        </FadeLink>
                    </div>
                </div>
            </div>

            <BookingPanel show={panelShow} sessions={sessions} onClose={() => setPanelShow(null)} />
        </section>
    )
}
